import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, userAuth } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = anonymous, object = logged in
  const [loading, setLoading] = useState(true);
  const [justLoggedIn, setJustLoggedIn] = useState(false); // signals for the login referral popup

  const refresh = useCallback(async () => {
    if (!userAuth.isAuthed()) { setUser(null); setLoading(false); return; }
    try {
      const r = await api.authMe();
      setUser(r.user);
    } catch {
      userAuth.clear();
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loginUser = useCallback(async (mobile, password) => {
    const r = await api.loginUser(mobile, password);
    userAuth.setToken(r.token);
    setUser(r.user);
    setJustLoggedIn(true);
    return r.user;
  }, []);

  const registerUser = useCallback(async (payload) => {
    const r = await api.registerUser(payload);
    userAuth.setToken(r.token);
    setUser(r.user);
    return r.user;
  }, []);

  const updateMe = useCallback(async (patch) => {
    const r = await api.authUpdateMe(patch);
    setUser(r.user);
    return r.user;
  }, []);

  const setupRecovery = useCallback(async (numbers, imageIds) => {
    const r = await api.setupRecovery(numbers, imageIds);
    setUser(r.user);
    return r.user;
  }, []);

  const personalize = useCallback(async (lucky_number, avatar_id) => {
    const r = await api.personalize(lucky_number, avatar_id);
    setUser(r.user);
    return r.user;
  }, []);

  const logout = useCallback(() => {
    userAuth.clear();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, isAuthed: !!user, justLoggedIn, clearJustLoggedIn: () => setJustLoggedIn(false), loginUser, registerUser, updateMe, setupRecovery, personalize, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
